import React, { useState, useEffect, useCallback } from 'react';
import './SystemDiagnosticsPanel.css';
import { API_BASE } from '../config';
import { useAuth } from '../context/AuthContext';

const formatUptime = (seconds) => {
  if (!seconds || seconds <= 0) return 'Just started';
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  const parts = [];
  if (d > 0) parts.push(`${d}d`);
  if (h > 0) parts.push(`${h}h`);
  if (m > 0) parts.push(`${m}m`);
  parts.push(`${s}s`);
  return parts.join(' ');
};

const SystemDiagnosticsPanel = () => {
  const { token } = useAuth();
  const [diagnostics, setDiagnostics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastRefreshed, setLastRefreshed] = useState(null);
  const [copied, setCopied] = useState(false);

  const fetchDiagnostics = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/dashboard/diagnostics`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      if (!res.ok) {
        throw new Error(`Diagnostics request failed (${res.status})`);
      }
      const data = await res.json();
      setDiagnostics(data);
      setLastRefreshed(new Date());
    } catch (err) {
      console.error('Diagnostics error:', err);
      setError(err.message || 'Failed to fetch diagnostics');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchDiagnostics();
  }, [fetchDiagnostics]);

  const copyPrometheusUrl = () => {
    const url = `${API_BASE}/actuator/prometheus`;
    navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  if (!token) {
    return (
      <div className="diagnostics-panel" role="region" aria-label="System Diagnostics">
        <p className="text-muted">Authentication required to view system diagnostics.</p>
      </div>
    );
  }

  const pool = diagnostics?.connectionPool || {};
  const heapUsed = diagnostics?.heapUsedMb || 0;
  const heapMax = diagnostics?.heapMaxMb || 1;
  const heapPercent = Math.min(100, Math.round((heapUsed / Math.max(1, heapMax)) * 100));

  return (
    <div className="diagnostics-panel" role="region" aria-label="System Diagnostics">
      <div className="diagnostics-header">
        <div>
          <h3 className="section-title">System Health & Diagnostics</h3>
          <p className="section-subtitle">
            Real-time telemetry, JVM memory metrics, database connection pools, and Prometheus export status.
          </p>
        </div>
        <button
          className="btn btn-secondary btn-refresh-diag"
          onClick={fetchDiagnostics}
          disabled={loading}
        >
          {loading ? 'Refreshing...' : '🔄 Refresh Metrics'}
        </button>
      </div>

      {lastRefreshed && (
        <div className="diagnostics-timestamp">
          Last synced: {lastRefreshed.toLocaleTimeString()}
        </div>
      )}

      {error && (
        <div className="diagnostics-error-alert" role="alert">
          ⚠️ {error}
        </div>
      )}

      {diagnostics && (
        <>
          {/* Status Banner */}
          <div className="diag-banner-card">
            <div className="status-indicator-group">
              <span className="pulse-dot active" />
              <div>
                <span className="banner-status-title">System Status: {diagnostics.status}</span>
                <span className="banner-uptime">Uptime: {formatUptime(diagnostics.uptimeSeconds)}</span>
              </div>
            </div>
            <div className="banner-meta-group">
              <span className="badge-meta">Role: {diagnostics.effectiveRole || 'USER'}</span>
              <span className="badge-meta">Plan: {diagnostics.tenantPlan || 'FREE'}</span>
            </div>
          </div>

          {/* Key Metric Gauges Grid */}
          <div className="diag-grid">
            {/* JVM Memory Gauge */}
            <div className="diag-card">
              <div className="diag-card-title">
                <span>☕ JVM Heap Memory</span>
                <span className="diag-card-badge">{heapPercent}%</span>
              </div>
              <div className="diag-stat-large">
                {heapUsed.toLocaleString()} <span className="stat-unit">MB</span>
                <span className="stat-limit">/ {heapMax.toLocaleString()} MB Max</span>
              </div>
              <div className="diag-progress-track">
                <div
                  className={`diag-progress-fill ${heapPercent > 85 ? 'danger' : heapPercent > 65 ? 'warning' : 'healthy'}`}
                  style={{ width: `${heapPercent}%` }}
                />
              </div>
              <div className="diag-card-footer">
                <span>Processors: {diagnostics.availableProcessors || 1} vCPUs</span>
                <span>Threads: {diagnostics.threadCount || 0} (Peak: {diagnostics.peakThreadCount || 0})</span>
              </div>
            </div>

            {/* HikariCP Connection Pool */}
            <div className="diag-card">
              <div className="diag-card-title">
                <span>🗄️ Database Connection Pool (HikariCP)</span>
                <span className="diag-card-badge healthy">Pooled</span>
              </div>
              <div className="diag-stat-large">
                {pool.activeConnections || 0} <span className="stat-unit">Active</span>
                <span className="stat-limit">/ {pool.maximumPoolSize || 10} Max Pool</span>
              </div>
              <div className="diag-pool-details">
                <div className="pool-stat-col">
                  <span className="pool-label">Idle</span>
                  <span className="pool-val">{pool.idleConnections || 0}</span>
                </div>
                <div className="pool-stat-col">
                  <span className="pool-label">Total</span>
                  <span className="pool-val">{pool.totalConnections || 0}</span>
                </div>
                <div className="pool-stat-col">
                  <span className="pool-label">Awaiting</span>
                  <span className="pool-val">{pool.threadsAwaitingConnection || 0}</span>
                </div>
              </div>
              <div className="diag-card-footer">
                <span>State: Connection pool healthy and non-blocking</span>
              </div>
            </div>

            {/* Tokenization & Gateway Throughput */}
            <div className="diag-card">
              <div className="diag-card-title">
                <span>⚡ Real-Time Processing Metrics</span>
                <span className="diag-card-badge">Micrometer</span>
              </div>
              <div className="diag-throughput-rows">
                <div className="throughput-item">
                  <span className="throughput-label">Tokens Processed</span>
                  <span className="throughput-value highlight">
                    {(diagnostics.totalTokensProcessed || 0).toLocaleString()}
                  </span>
                </div>
                <div className="throughput-item">
                  <span className="throughput-label">Zero-Trust LLM Gateway Invocations</span>
                  <span className="throughput-value">
                    {(diagnostics.totalGatewayCalls || 0).toLocaleString()}
                  </span>
                </div>
              </div>
              <div className="diag-card-footer">
                <span>Tracked via custom dimensional meters</span>
              </div>
            </div>
          </div>

          {/* Prometheus & Tracing Integration Details */}
          <div className="diag-infra-section">
            <div className="infra-card">
              <div className="infra-card-header">
                <h4>📈 Prometheus Scrape Exporter</h4>
                <button
                  className="btn-copy-mini"
                  onClick={copyPrometheusUrl}
                  title="Copy Prometheus Endpoint"
                >
                  {copied ? '✓ Copied URL' : '📋 Copy Scrape URL'}
                </button>
              </div>
              <p className="infra-desc">
                Prometheus metrics exporter is exposed at <code>{diagnostics.prometheusEndpoint}</code>. Scrape this target in Grafana, Prometheus Agent, or Datadog for automated alerting and SLA dashboards.
              </p>
              <div className="cli-snippet">
                <code>curl -s http://localhost:8080{diagnostics.prometheusEndpoint} | grep vaultize</code>
              </div>
            </div>

            <div className="infra-card">
              <div className="infra-card-header">
                <h4>🔍 Distributed Request Tracing (MDC)</h4>
                <span className="badge-meta">Active</span>
              </div>
              <p className="infra-desc">
                Every HTTP request carries a unique <code>X-Trace-Id</code> header across filters, service boundaries, and SLF4J MDC structured logs. Trace IDs correlate client API calls with database logs and audit trails.
              </p>
              <div className="cli-snippet">
                <code>Header: X-Trace-Id: tr_&lt;16-char-uuid&gt;</code>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default SystemDiagnosticsPanel;
